import React from "react";
import {
  AbstractFilters,
  AbstractRow,
} from "src/components/ReactiveTable/RtTypes";

import { TableCell } from "../cell";
import { HeaderCell } from "../cell/HeaderCell";
import { TableFooter } from "../footer";
import { TableRow } from "../row";
import { TableHead } from "../row/TableHead";
import { SelectionCell, SelectionHeader } from "../selection";
import type {
  PropsHeaderCell,
  PropsSelectionCell,
  PropsSelectionHeader,
  PropsTableCell,
  PropsTableFooter,
  PropsTableHead,
  PropsTableRow,
} from "./componentProps";

/**
 * Конфигурация компонента Head
 */
type ComponentConfigHead<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> = {
  name: "Head";
  props: PropsTableHead<TRow, TFilters>;
};

/**
 * Конфигурация компонента Footer
 */
type ComponentConfigFooter = {
  name: "Footer";
  props: PropsTableFooter;
};

/**
 * Конфигурация компонента Row
 */
type ComponentConfigRow<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> = {
  name: "Row";
  props: PropsTableRow<TRow, TFilters>;
};

/**
 * Конфигурация компонента Cell
 */
type ComponentConfigCell<TRow extends AbstractRow> = {
  name: "Cell";
  props: PropsTableCell<TRow>;
};

/**
 * Конфигурация компонента HeaderCell
 */
type ComponentConfigHeaderCell<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> = {
  name: "HeaderCell";
  props: PropsHeaderCell<TRow, TFilters>;
};

/**
 * Конфигурация компонента SelectionCell
 */
type ComponentConfigSelectionCell<TRow extends AbstractRow> = {
  name: "SelectionCell";
  props: PropsSelectionCell<TRow>;
};

/**
 * Конфигурация компонента SelectionHeader
 */
type ComponentConfigSelectionHeader<TRow extends AbstractRow> = {
  name: "SelectionHeader";
  props: PropsSelectionHeader<TRow>;
};

/**
 * Объединенный тип всех конфигураций компонентов таблицы
 */
type TableComponentConfig<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> =
  | ComponentConfigCell<TRow>
  | ComponentConfigFooter
  | ComponentConfigHead<TRow, TFilters>
  | ComponentConfigHeaderCell<TRow, TFilters>
  | ComponentConfigRow<TRow, TFilters>
  | ComponentConfigSelectionCell<TRow>
  | ComponentConfigSelectionHeader<TRow>;

/**
 * Имя компонента таблицы
 */
export type TableComponentName<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> = TableComponentConfig<TRow, TFilters>["name"];

/**
 * Извлекает конфигурацию компонента по его имени
 */
export type ComponentConfigFor<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
  K extends TableComponentName<TRow, TFilters>,
> = Extract<TableComponentConfig<TRow, TFilters>, { name: K }>;

/**
 * Тип функции рендеринга компонента таблицы
 */
export type TableComponentRenderer<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
  K extends TableComponentName<TRow, TFilters>,
> = React.FC<ComponentConfigFor<TRow, TFilters, K>["props"]>;

/**
 * Реестр компонентов таблицы (маппинг имя -> компонент)
 */
export type TableComponents<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> = {
  [K in TableComponentName<TRow, TFilters>]: TableComponentRenderer<
    TRow,
    TFilters,
    K
  >;
};

/**
 * Частичный тип для кастомизации компонентов
 */
export type PartialTableComponents<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> = Partial<TableComponents<TRow, TFilters>>;

/**
 * Создает реестр компонентов таблицы с дефолтными реализациями
 */
export const createComponentRendererRegistry = <
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
>(): TableComponents<TRow, TFilters> => ({
  Head: TableHead,
  Footer: TableFooter,
  Row: TableRow,
  Cell: TableCell,
  HeaderCell,
  SelectionCell,
  SelectionHeader,
});
